// @ts-check
/* eslint-env browser */

/** @typedef {import('@geoman-io/maplibre-geoman-free').GeoJsonImportFeature} GeoJsonImportFeature */

import * as turf from '@turf/turf'
import { getVectorLayerIds } from './vector-tile-utils.js'
import { reactiveStore } from '../reactive-store.js'
import { getGeomanFeatureByPropertyId } from './edit-feature.js'

/**
 * Handles magic wand selection - clicking on the map to select surrounding area based on vector tile bounds
 */
export class MagicWand {
  #MAX_FEATURES = 50

  #map
  #geoman
  /** @type {((event: any) => void) | null} */
  #clickHandler = null

  /**
   * @param {import('maplibre-gl').Map} map
   * @param {import('@geoman-io/maplibre-geoman-free').Geoman} geoman
   */
  constructor (map, geoman) {
    this.#map = map
    this.#geoman = geoman
  }

  /**
   * Filters out Geoman features from query results
   * @param {any[]} features
   * @returns {any[]}
   */
  #filterGeomanFeatures (features) {
    return features.filter((feature) => {
      const source = feature.source
      return (
        source !== 'gm_main' &&
        source !== 'gm_temporary' &&
        source !== undefined
      )
    })
  }

  /**
   * Queries vector tile features at a point and extracts their geometries
   * @param {number} lng
   * @param {number} lat
   * @returns {any[]}
   */
  #getFeaturesAtPoint (lng, lat) {
    const point = this.#map.project([lng, lat])

    // First try to get fill layers (polygons)
    const fillLayers = getVectorLayerIds(this.#map, { fillOnly: true })
    if (fillLayers.length > 0) {
      const fillFeatures = this.#map.queryRenderedFeatures(point, {
        layers: fillLayers
      })

      const vectorFeatures = this.#filterGeomanFeatures(fillFeatures)
      if (vectorFeatures.length > 0) {
        return vectorFeatures.slice(0, this.#MAX_FEATURES)
      }
    }

    // If no fill features found, try line layers
    const allLayers = getVectorLayerIds(this.#map)
    const lineLayers = allLayers.filter((id) => !fillLayers.includes(id))
    if (lineLayers.length > 0) {
      const lineFeatures = this.#map.queryRenderedFeatures(point, {
        layers: lineLayers
      })

      const vectorFeatures = this.#filterGeomanFeatures(lineFeatures)
      return vectorFeatures.slice(0, this.#MAX_FEATURES)
    }

    return []
  }

  /**
   * Selects the best polygon from multiple polygons
   * Tries to find a polygon that contains the click point
   * @param {any[]} polygons
   * @param {number} clickLng
   * @param {number} clickLat
   * @returns {any | null}
   */
  #selectBestPolygon (polygons, clickLng, clickLat) {
    if (polygons.length === 0) return null
    if (polygons.length === 1) return polygons[0]

    const clickPoint = turf.point([clickLng, clickLat])

    // Try to find a polygon that contains the click point
    const containingPolygon = polygons.find((polygon) => {
      const polygonFeature = turf.polygon(polygon.coordinates)
      return turf.booleanPointInPolygon(clickPoint, polygonFeature)
    })

    // If no polygon contains the point, return the first one
    return containingPolygon ?? polygons[0]
  }

  /**
   * Checks if a point is inside a feature's geometry
   * @param {any} feature - Feature from reactiveStore or Geoman
   * @param {[number, number]} point - [lng, lat]
   * @returns {boolean}
   */
  #isPointInFeature (feature, point) {
    if (!feature || !feature.geometry) return false

    const geometry = feature.geometry
    if (this.#isEmptyGeometry(geometry)) return false

    const pointFeature = turf.point(point)

    if (geometry.type === 'Polygon') {
      return turf.booleanPointInPolygon(pointFeature, turf.polygon(geometry.coordinates))
    } else if (geometry.type === 'MultiPolygon') {
      return turf.booleanPointInPolygon(pointFeature, turf.multiPolygon(geometry.coordinates))
    }

    return false
  }

  /**
   * Checks if a geometry is empty (has no coordinates)
   * @param {any} geometry - The geometry to check
   * @returns {boolean}
   */
  #isEmptyGeometry (geometry) {
    if (!geometry) return true
    if (geometry.type === 'Polygon') {
      return geometry.coordinates.length === 0 ||
        geometry.coordinates.every(ring => ring.length === 0)
    }
    if (geometry.type === 'MultiPolygon') {
      return geometry.coordinates.length === 0 ||
        geometry.coordinates.every(polygon => polygon.every(ring => ring.length === 0))
    }
    return false
  }

  /**
   * Checks if a polygon has valid coordinates
   * @param {any} polygon - The polygon to check
   * @returns {boolean}
   */
  #isValidPolygon (polygon) {
    if (!polygon || polygon.type !== 'Polygon') return false
    if (!polygon.coordinates || !Array.isArray(polygon.coordinates)) return false
    if (polygon.coordinates.length === 0) return false
    const exteriorRing = polygon.coordinates[0]
    if (!Array.isArray(exteriorRing) || exteriorRing.length < 4) return false
    return true
  }

  /**
   * Subtracts a polygon from an existing feature geometry
   * @param {any} existingGeometry - The geometry to subtract from
   * @param {any} subtractPolygon - The polygon to subtract
   * @returns {any | null} - The resulting geometry or null if subtraction fails
   */
  #subtractPolygon (existingGeometry, subtractPolygon) {
    if (!existingGeometry || !subtractPolygon) return null

    try {
      const existingFeature = turf.feature(existingGeometry)
      const subtractFeature = turf.polygon(subtractPolygon.coordinates)

      // Check if they intersect
      if (!turf.booleanIntersects(existingFeature, subtractFeature)) {
        return null
      }

      // Perform difference operation
      const featureCollection = turf.featureCollection([existingFeature, subtractFeature])
      const difference = turf.difference(featureCollection)

      if (!difference || !difference.geometry) {
        return null
      }

      return difference.geometry
    } catch (error) {
      return null
    }
  }

  /**
   * Handles click event to select surrounding area
   * @param {any} event
   */
  #handleClick (event) {
    const mouseEvent = event
    if (!mouseEvent.lngLat) {
      return
    }

    const clickLng = mouseEvent.lngLat.lng
    const clickLat = mouseEvent.lngLat.lat

    // Query features at the click point
    const features = this.#getFeaturesAtPoint(clickLng, clickLat)
    if (features.length === 0) {
      return
    }

    // Extract polygon geometries from features
    const polygons = []
    for (const feature of features) {
      const geometry = feature.geometry
      if (!geometry) continue

      if (geometry.type === 'Polygon') {
        if (geometry.coordinates?.length > 0 && geometry.coordinates[0]?.length >= 4) {
          polygons.push(geometry)
        }
      } else if (
        geometry.type === 'MultiPolygon' &&
        geometry.coordinates.length > 0 &&
        geometry.coordinates[0]?.length > 0
      ) {
        const firstRing = geometry.coordinates[0][0]
        if (firstRing?.length >= 4) {
          polygons.push({
            type: 'Polygon',
            coordinates: geometry.coordinates[0]
          })
        }
      }
    }

    if (polygons.length === 0) {
      return
    }

    // Select the best polygon (one that contains the click point, or the first one)
    const selectedPolygon = this.#selectBestPolygon(
      polygons,
      clickLng,
      clickLat
    )
    if (!selectedPolygon || !this.#isValidPolygon(selectedPolygon)) {
      return
    }

    // Check if there's a selected feature to merge with or subtract from
    const selectedFeatureId = reactiveStore.selectedFeatureId.get()
    const currentFeatures = reactiveStore.features.get()
    const existingReactiveFeature = selectedFeatureId
      ? currentFeatures.find(f => f.properties?._id === selectedFeatureId)
      : null

    // Check if feature exists in Geoman (has geometry)
    const existingGeomanFeature = selectedFeatureId
      ? getGeomanFeatureByPropertyId(this.#geoman, selectedFeatureId)
      : null

    /** @type {[number, number]} */
    const clickPoint = [clickLng, clickLat]

    // Check if click point is inside an existing feature (for subtraction)
    const isClickInsideFeature = existingGeomanFeature
      ? this.#isPointInFeature(existingGeomanFeature.getGeoJson(), clickPoint)
      : existingReactiveFeature && existingReactiveFeature.geometry
        ? this.#isPointInFeature(existingReactiveFeature, clickPoint)
        : false

    if (existingGeomanFeature && isClickInsideFeature) {
      // Click is inside existing feature - subtract
      const existingGeoJson = existingGeomanFeature.getGeoJson()
      const resultGeometry = this.#subtractPolygon(existingGeoJson.geometry, selectedPolygon)

      if (resultGeometry) {
        // Subtraction successful - update feature
        /** @type {GeoJsonImportFeature} */
        const updatedFeature = {
          type: 'Feature',
          geometry: resultGeometry,
          properties: { ...existingGeoJson.properties }
        }
        delete updatedFeature.properties.__gm_id

        const updatedFeatures = currentFeatures.map(feature =>
          feature.properties?._id === selectedFeatureId ? updatedFeature : feature
        )

        reactiveStore.features.set(updatedFeatures)
        this.#forceEditControlsUpdate()
      } else {
        // Subtraction failed - remove entire feature (set geometry to empty polygon)
        const updatedFeatures = currentFeatures.map(feature =>
          feature.properties?._id === selectedFeatureId
            ? { ...feature, geometry: { type: 'Polygon', coordinates: [] } }
            : feature
        )
        reactiveStore.features.set(updatedFeatures)
        this.#forceEditControlsUpdate()
      }
    } else if (existingGeomanFeature) {
      // Feature exists in Geoman but click is outside - merge with existing geometry
      const existingGeoJson = existingGeomanFeature.getGeoJson()

      // If existing geometry is empty, just replace it with the selected polygon
      if (this.#isEmptyGeometry(existingGeoJson.geometry)) {
        /** @type {GeoJsonImportFeature} */
        const newFeature = {
          type: 'Feature',
          geometry: selectedPolygon,
          properties: { ...existingGeoJson.properties }
        }
        delete newFeature.properties.__gm_id

        const updatedFeatures = currentFeatures.map(feature =>
          feature.properties?._id === selectedFeatureId ? newFeature : feature
        )
        reactiveStore.features.set(updatedFeatures)
        this.#forceEditControlsUpdate()
        return
      }

      const merged = turf.union(turf.featureCollection([turf.feature(existingGeoJson.geometry), turf.feature(selectedPolygon)]))
      if (!merged?.geometry) {
        return
      }

      // Create merged feature with existing properties
      /** @type {GeoJsonImportFeature} */
      const mergedFeature = {
        type: 'Feature',
        geometry: merged.geometry,
        properties: { ...existingGeoJson.properties }
      }
      delete mergedFeature.properties.__gm_id

      // Get current features from reactiveStore and replace the existing one with merged
      const updatedFeatures = currentFeatures.map(feature =>
        feature.properties?._id === selectedFeatureId ? mergedFeature : feature
      )

      // Update reactiveStore - this will automatically trigger #showFeatures
      reactiveStore.features.set(updatedFeatures)
      this.#forceEditControlsUpdate()
    } else if (existingReactiveFeature) {
      // Feature exists in reactiveStore but not in Geoman (may have null or empty geometry - deleted feature)
      const existingGeometry = existingReactiveFeature.geometry

      if (existingGeometry === null || this.#isEmptyGeometry(existingGeometry)) {
        // Feature was deleted (has null or empty geometry) - restore it by adding the new geometry
        /** @type {GeoJsonImportFeature} */
        const restoredFeature = {
          type: 'Feature',
          geometry: selectedPolygon,
          properties: { ...existingReactiveFeature.properties }
        }
        delete restoredFeature.properties.__gm_id

        // Replace the feature with restored geometry
        const updatedFeatures = currentFeatures.map(feature =>
          feature.properties?._id === selectedFeatureId ? restoredFeature : feature
        )

        reactiveStore.features.set(updatedFeatures)
        this.#forceEditControlsUpdate()
      } else {
        // Feature has valid geometry but not in Geoman (shouldn't happen, but handle it)
        // Merge with existing geometry from reactiveStore
        const merged = turf.union(turf.featureCollection([turf.feature(existingGeometry), turf.feature(selectedPolygon)]))
        if (!merged?.geometry) {
          return
        }

        /** @type {GeoJsonImportFeature} */
        const mergedFeature = {
          type: 'Feature',
          geometry: merged.geometry,
          properties: { ...existingReactiveFeature.properties }
        }
        delete mergedFeature.properties.__gm_id

        const updatedFeatures = currentFeatures.map(feature =>
          feature.properties?._id === selectedFeatureId ? mergedFeature : feature
        )

        reactiveStore.features.set(updatedFeatures)
        this.#forceEditControlsUpdate()
      }
    } else {
      // No selected feature - add as new feature
      // Note: This creates a feature without _id which may cause issues
      // TODO: Generate proper _id and feature_type for new features
      /** @type {GeoJsonImportFeature} */
      const featureToAdd = {
        type: 'Feature',
        geometry: selectedPolygon,
        properties: {}
      }
      // Remove Geoman internal ID if present
      delete featureToAdd.properties.__gm_id

      // Get current features and add the new one
      reactiveStore.features.set([...currentFeatures, featureToAdd])
      this.#forceEditControlsUpdate()
    }
  }

  /**
   * Enables magic wand selection
   */
  enable () {
    if (this.#clickHandler) {
      return
    }
    this.#clickHandler = (event) => {
      this.#handleClick(event)
    }
    this.#map.on('click', this.#clickHandler)
  }

  /**
   * Re-filters edit controls for the current feature without resetting the edit mode.
   * Uses setTimeout to ensure showFeatures has completed.
   */
  #forceEditControlsUpdate () {
    setTimeout(() => {
      const editControls = document.querySelector('edit-controls')
      if (editControls && typeof editControls.refreshOptionsForCurrentFeature === 'function') {
        editControls.refreshOptionsForCurrentFeature()
      }
    }, 0)
  }

  /**
   * Disables magic wand selection
   */
  disable () {
    if (!this.#clickHandler) {
      return
    }
    this.#map.off('click', this.#clickHandler)
    this.#clickHandler = null
  }
}
